import { useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { assetUrl } from "@/lib/api";
import { LogoMark } from "./Logo";

const D = 30; // book depth in px (spine/pages)
const H = D / 2;

function PlaceholderCover({ title, author }) {
  return (
    <div className="absolute inset-0 bg-gradient-to-b from-[#161311] to-[#0a0a0a] border border-gold/25 flex flex-col items-center justify-between py-8 px-6 text-center">
      <span className="font-ui text-[9px] tracking-[0.4em] text-gold/80 uppercase">Volume One</span>
      <div>
        <LogoMark size={40} className="mx-auto mb-5 opacity-90" />
        <p className="font-display font-black text-paper text-xl sm:text-2xl leading-tight tracking-wide">
          DIRTY CHURCH<br />CHRONICLES
        </p>
        <span className="block w-10 h-px bg-crimson mx-auto my-4" />
        <p className="font-reading italic text-paper/60 text-sm">111 Chronicles</p>
      </div>
      <span className="font-ui text-[9px] tracking-[0.3em] text-paper/50 uppercase">{author}</span>
    </div>
  );
}

export default function Book3D({ cover, spine, back, title = "Dirty Church Chronicles", author = "Jermaine Miller" }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const interactive =
    !reduce && typeof window !== "undefined" && window.matchMedia("(hover: hover)").matches;
  const [tilt, setTilt] = useState(null);

  const onMove = (e) => {
    if (!interactive || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const dx = (e.clientX - r.left) / r.width - 0.5;
    const dy = (e.clientY - r.top) / r.height - 0.5;
    setTilt(`rotateY(${-16 + dx * 16}deg) rotateX(${5 - dy * 10}deg)`);
  };
  const onLeave = () => setTilt(null);

  const transform = tilt || "rotateY(-16deg) rotateX(5deg)";

  return (
    <div
      ref={ref}
      className="relative inline-block py-6 px-10"
      style={{ perspective: "1200px" }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid="book-3d"
    >
      <div className="absolute inset-0 book-spotlight" aria-hidden="true" />
      <div
        className="relative w-[220px] h-[330px] sm:w-[290px] sm:h-[435px]"
        style={{
          transformStyle: "preserve-3d",
          transform,
          transition: "transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* back cover — real back artwork when supplied */}
        <div
          className="absolute inset-0 bg-[#0a0a0a] border border-paper/10 rounded-sm overflow-hidden"
          style={{ transform: `rotateY(180deg) translateZ(${H}px)` }}
          aria-hidden="true"
        >
          {back && (
            <img src={assetUrl(back)} alt="" className="w-full h-full object-cover" loading="lazy" />
          )}
        </div>
        {/* spine — real spine artwork when supplied, neutral factual lettering otherwise */}
        <div
          className="absolute top-0 h-full bg-[#141414] border-y border-l border-gold/20 overflow-hidden"
          style={{ left: `-${H}px`, width: `${D}px`, transform: "rotateY(-90deg)" }}
          aria-hidden="true"
        >
          {spine ? (
            <img src={assetUrl(spine)} alt="" className="w-full h-full" style={{ objectFit: "fill" }} loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span
                className="font-display text-gold/90 text-[9px] tracking-[0.3em] uppercase whitespace-nowrap"
                style={{ writingMode: "vertical-rl" }}
              >
                {title} · {author}
              </span>
            </div>
          )}
        </div>
        {/* page block edge */}
        <div
          className="absolute top-[2px] h-[calc(100%-4px)]"
          style={{
            right: `-${H}px`,
            width: `${D}px`,
            transform: "rotateY(90deg)",
            background: "repeating-linear-gradient(to right, #d9d2c3 0px, #efe9dc 2px, #cfc7b6 3px)",
          }}
          aria-hidden="true"
        />
        {/* front cover — official artwork */}
        <div
          className="absolute inset-0 rounded-r-sm overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]"
          style={{ transform: `translateZ(${H}px)` }}
        >
          {cover ? (
            <img
              src={assetUrl(cover)}
              alt={`Cover of ${title} by ${author}`}
              className="w-full h-full object-cover"
              data-testid="book-cover-image"
            />
          ) : (
            <PlaceholderCover title={title} author={author} />
          )}
        </div>
      </div>
      <div
        className="mx-auto mt-8 h-6 w-3/4 rounded-[50%] bg-black/70 blur-xl"
        aria-hidden="true"
      />
    </div>
  );
}
